import "./App.css";
import React, { useEffect, useRef, useState } from "react";
import { Alert, Backdrop, CircularProgress, Grid, Snackbar } from "@mui/material";

import { Route, useLocation, useHistory, Switch } from "react-router-dom";
import { LandingPage } from "./components/LandingPage/LandingPage";
import Error from "./Error";
import { Registration } from "./components/Registration/Registration";
import { TopBar } from "./components/TopBar/Topbar";
import MainPage from "./components/MainPage/MainPage";
import EAPForm from "./components/EAPForm/EAPForm";
import { TermsCond } from "./components/TermsCond/TermsCond";

import { ConsentRegistration } from "./components/Consent/ConsentRegistration";
import { UserPage } from "./components/UserPage/UserPage";
import { Password } from "./components/Password/Password";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import {
  selectAccount,
  selectErrMsg,
  selectMsg,
  setErrMsg,
  setMsg,
} from "./redux/UserSlice";
import {
  selectErrMsgForm,
  selectFormMsg,
  setFormErrMsg,
  setFormMsg,
} from "./redux/FormsSlice";
import { SubmitExpense } from "./components/SubmitExpense/SubmitExpense";
import { PasswordReminder } from "./PasswordReminder/PasswordReminder";
import { stopLoading } from "./redux/QuestionnaireSlice";
import {
  selectErrMsgFiles,
  selectFilesMsg,
  selectIsLoading,
  setFilesErrMsg,
  setFilesMsg,
} from "./components/FileUploader/FileUploaderSlice";
import Footer from "./Footer";
import { dark_blue } from "./theme";
import { Confirmation } from "./Confirmation";
import { logoutAPI, ping } from "./redux/UserAPI";
import { PageNotFound } from "./PageNotFound/PageNotFound";
import { SetPassword } from "./components/SetPassword/SetPassword";
import { UnderConstruction } from "./components/UnderConstruction/UnderConstruction";
import { getAppStatus } from "./redux/appAPI";
import { selectUnderConstruction } from "./redux/appSlice";

const INACTIVITY_TIMEOUT_MS =
  process.env.REACT_APP_ENV === "production" ? 30 * 60 * 1000 : 5 * 60 * 1000;

function App() {
  const dispatch = useAppDispatch();

  const account = useAppSelector(selectAccount);
  const routes: string[] = [];
  const location = useLocation();
  routes.push(location.pathname);
  const history = useHistory();
  const err_msg = useAppSelector(selectErrMsg);
  const msg = useAppSelector(selectMsg);
  const formErrMsg = useAppSelector(selectErrMsgForm);
  const formMsg = useAppSelector(selectFormMsg);
  const filesErrMsg = useAppSelector(selectErrMsgFiles);
  const filesMsg = useAppSelector(selectFilesMsg);
  const isLoading = useAppSelector(selectIsLoading);
  const underConstruction = useAppSelector(selectUnderConstruction)
  const [isModalOpen, setModalDisplay] = useState<boolean>(false);

  const [display, setDisplay] = useState(false);

  const [isUserActive, setIsUserActive] = useState(true);
  const [showInactivityMessage, setShowInactivityMessage] = useState(false);
  useEffect(() => {
    if (account) {
      let activityTimeout: NodeJS.Timeout;

      const resetActivityTimeout = () => {
        if (activityTimeout) {
          clearTimeout(activityTimeout);
        }

        activityTimeout = setTimeout(() => {
          setIsUserActive(false);
        }, INACTIVITY_TIMEOUT_MS);
      };

      const handleUserActivity = () => {
        setIsUserActive(true);
        resetActivityTimeout();
      };

      window.addEventListener("mousemove", handleUserActivity);
      window.addEventListener("mousedown", handleUserActivity);
      window.addEventListener("mouseup", handleUserActivity);
      window.addEventListener("keydown", handleUserActivity);

      resetActivityTimeout();

      return () => {
        window.removeEventListener("mousemove", handleUserActivity);
        window.removeEventListener("mousedown", handleUserActivity);
        window.removeEventListener("mouseup", handleUserActivity);
        window.removeEventListener("keydown", handleUserActivity);
        clearTimeout(activityTimeout);
      };
    }
    if (!account && routes[0] !== "/form") {
      dispatch(getAppStatus('a'))
    }
  }, [account]);

  useEffect(() => {
  dispatch(getAppStatus('a'))
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (!isUserActive) {
      dispatch(logoutAPI(true));
      history.push("/");
      setShowInactivityMessage(true);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isUserActive]);

  const handleInactivityMessageClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setShowInactivityMessage(false);
  };

  const closeModal = () => {
    dispatch(setMsg(null));
    dispatch(setErrMsg(null));
    dispatch(setFormMsg(null));
    dispatch(setFormErrMsg(null));
    dispatch(setFilesMsg(null));
    dispatch(setFilesErrMsg(null));
    if (
      msg === "Password successfully reset" ||
      err_msg === "Session timeout. Please login again." ||
      err_msg === "Cannot connect to server"
    ) {
      history.push("/");
    }
    setModalDisplay(false);
  };



  useEffect(() => {
    if (routes[0] === "/landing_page") return;
    if (msg || err_msg || formErrMsg || formMsg || filesErrMsg || filesMsg) {
      setModalDisplay(true);
      dispatch(stopLoading());
    }
  }, [msg, err_msg, formErrMsg, formMsg]);

  const width = window.screen.width;

  return (
    <Grid container sx={{ width: "100%" }}>
      <Grid item xs={12}>
        <TopBar />
        <Backdrop
          sx={{
            zIndex: 8000,
            color: "#fff",
          }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Error
          formMsg={formMsg}
          msg={msg}
          formErrMsg={formErrMsg}
          err_msg={err_msg}
          filesMsg={filesMsg}
          filesErrMsg={filesErrMsg}
          display={isModalOpen}
          handleModal={closeModal}
        />
        <Confirmation display={display} />
        <Snackbar
          open={showInactivityMessage}
          autoHideDuration={5000}
          onClose={handleInactivityMessageClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setShowInactivityMessage(false)}
            severity="warning"
            variant="filled"
            sx={{ width: "100%" }}
          >
            You were logged out due to inactivity
          </Alert>
        </Snackbar>
        <Switch>
          <Route exact path="/">
           {underConstruction ? <UnderConstruction /> : <MainPage/>}
          </Route>
          <Route exact path="/registration">
            <Registration />
          </Route>
          <Route exact path="/form">
            <EAPForm />
          </Route>
          <Route exact path="/landing_page">
            <LandingPage />
          </Route>
          <Route exact path="/terms_conditions">
            <TermsCond />
          </Route>
          <Route exact path="/consent">
            <ConsentRegistration />
          </Route>
          <Route exact path="/user_page">
            <UserPage />
          </Route>
          <Route exact path="/reset_password/:token">
            <Password />
          </Route>
          <Route exact path="/set_password/:token">
            <SetPassword />
          </Route>
          <Route exact path="/submit_expense">
            <SubmitExpense account={account} />
          </Route>
          <Route exact path="/password_remind">
            <PasswordReminder />
          </Route>
          <Route path="*">
            <PageNotFound />
          </Route>
        </Switch>
        <Grid
          container
          sx={{
            height: "8vh",
            position: "fixed",
             width: "100%",
            bottom: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: dark_blue,
          }}
        >
          <Footer />
        </Grid>
      </Grid>
    </Grid>
  );
}

export default App;
